/**
 * Persistent auth storage — resilient against iOS Safari PWA eviction.
 *
 * Problem: modern mobile browsers aggressively clear ``localStorage`` for
 * installed PWAs after ~7 days of no interaction (iOS Safari does it by
 * spec; Chrome/Firefox less aggressively but still). Users open the app
 * a week later and get bounced back to login even though their JWT is
 * technically valid for 30 days.
 *
 * Multi-layered mitigation:
 *   1. Request persistent storage via ``navigator.storage.persist()``.
 *      In installed PWAs on Chrome/Edge/Firefox this is granted
 *      automatically → the browser will NOT evict our data.
 *   2. Mirror the auth token in IndexedDB so a partial eviction of
 *      localStorage (which iOS may still perform) doesn't kill the
 *      session — we re-hydrate from IDB on next visit.
 *   3. Expose ``loadToken()`` / ``saveToken()`` / ``clearToken()`` so the
 *      rest of the app doesn't have to know about the two backends.
 */

const KEY = "hosys_token";
const DB_NAME = "hosys_auth";
const STORE = "kv";

let _dbPromise = null;

function _openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("no idb"));
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function _idbGet(key) {
  try {
    const db = await _openDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function _idbSet(key, value) {
  try {
    const db = await _openDb();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = resolve;
      tx.onerror = resolve;
    });
  } catch {
    /* best effort */
  }
}

async function _idbDelete(key) {
  try {
    const db = await _openDb();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = resolve;
      tx.onerror = resolve;
    });
  } catch { /* best effort */ }
}

/** Ask the browser to protect our storage from automatic eviction.
    Installed PWAs on Chromium/Firefox get this granted automatically; on
    iOS it's a hint that reduces (but doesn't eliminate) eviction. */
export async function requestPersistentStorage() {
  try {
    if (navigator.storage && navigator.storage.persist) {
      const granted = await navigator.storage.persist();
      return granted;
    }
  } catch { /* not supported */ }
  return false;
}

/** Load the auth token. Tries localStorage first (synchronous is nicer
    for interceptors), falls back to IndexedDB and re-hydrates
    localStorage when found — this covers the exact iOS eviction case:
    localStorage was wiped but IDB survived, so on next visit we restore
    the token transparently and the user stays logged in. */
export async function loadTokenAsync() {
  try {
    const ls = localStorage.getItem(KEY);
    if (ls) return ls;
  } catch { /* SSR / private mode */ }
  const idb = await _idbGet(KEY);
  if (idb) {
    try { localStorage.setItem(KEY, idb); } catch { /* ignore */ }
    return idb;
  }
  return null;
}

/** Synchronous localStorage read — for axios interceptors that can't
    await. Use ``loadTokenAsync`` at app boot to hydrate first. */
export function loadTokenSync() {
  try { return localStorage.getItem(KEY); } catch { return null; }
}

/** Persist the token to BOTH backends. Fire-and-forget for IDB. */
export function saveToken(token) {
  try { localStorage.setItem(KEY, token); } catch { /* ignore */ }
  _idbSet(KEY, token);
  // Also request persistent storage on every save — the first call after
  // the app is installed is what actually matters.
  requestPersistentStorage();
}

/** Wipe from both backends. Fire-and-forget for IDB. */
export function clearToken() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  _idbDelete(KEY);
}
